/**
 * Data Hub — run stepper.
 *
 * Product-style circle indicator (same visual language as Assessment Hub →
 * Create Assessment), sized to stay readable across the longer Metrics Import
 * & Run path.
 *
 * Phase 1 (manual) and Phase 2 (auto) render as two separate cards so the
 * phases read as distinct stages, not one continuous strip.
 * Forward-only on the indicator itself (no click-to-jump).
 */
import { Fragment, type ReactNode } from "react";

import {
  ArrowUpCircle,
  BarChart3,
  Building2,
  CalendarRange,
  Check,
  Minus,
  FileSpreadsheet,
  Gauge,
  LayoutGrid,
  ShieldCheck,
  SlidersHorizontal,
  Table2,
  Tags,
  Target,
  Upload,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { RO_SCORES_ENABLED } from "../data/flags";

export type StepId =
  | "file"
  | "columns"
  | "accounts"
  | "metrics"
  | "categories"
  | "import"
  | "qc"
  | "heal"
  | "promote";

export const STEPS: { id: StepId; label: string; icon: LucideIcon }[] = [
  { id: "file", label: "File", icon: FileSpreadsheet },
  { id: "columns", label: "Columns", icon: Table2 },
  { id: "accounts", label: "Accounts", icon: Building2 },
  { id: "categories", label: "Categories", icon: Tags },
  { id: "metrics", label: "Metrics", icon: BarChart3 },
  { id: "import", label: "Import", icon: Upload },
  { id: "qc", label: "QC", icon: ShieldCheck },
  { id: "heal", label: "Heal", icon: Wrench },
  { id: "promote", label: "Promote", icon: ArrowUpCircle },
];

export type Phase2Step =
  | "benchmark"
  | "normalize"
  | "summarize"
  | "kpi_scores"
  | "ro_scores";

export const PHASE2_STEPS: {
  id: Phase2Step;
  label: string;
  icon: LucideIcon;
}[] = [
  { id: "benchmark", label: "Benchmark", icon: Gauge },
  { id: "normalize", label: "Normalize", icon: SlidersHorizontal },
  { id: "summarize", label: "Summarize", icon: CalendarRange },
  { id: "kpi_scores", label: "Category Scores", icon: LayoutGrid },
  ...(RO_SCORES_ENABLED
    ? ([{ id: "ro_scores", label: "R&O Scores", icon: Target }] as const)
    : []),
];

export type StepStatus =
  | "pending"
  | "running"
  | "success"
  | "skipped"
  | "failed";

type VisualState =
  | "done"
  | "current"
  | "todo"
  | "failed"
  | "skipped"
  | "running";

function circleClass(state: VisualState): string {
  const base =
    "flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all duration-300";
  switch (state) {
    case "done":
      return `${base} border-purple-600 bg-purple-600 text-white`;
    case "current":
      return `${base} border-purple-600 bg-white text-purple-600`;
    case "running":
      return `${base} border-purple-600 bg-white text-purple-600 ring-2 ring-purple-200 animate-pulse`;
    case "failed":
      return `${base} border-red-500 bg-red-50 text-red-600`;
    case "skipped":
      return `${base} border-amber-300 bg-amber-50 text-amber-700`;
    default:
      return `${base} border-gray-300 bg-white text-gray-400`;
  }
}

function labelClass(state: VisualState): string {
  const base =
    "mt-1.5 max-w-[4.25rem] text-center text-xs font-medium leading-tight";
  switch (state) {
    case "current":
    case "running":
      return `${base} text-purple-600`;
    case "done":
      return `${base} text-gray-900`;
    case "failed":
      return `${base} text-red-600`;
    case "skipped":
      return `${base} text-amber-700`;
    default:
      return `${base} text-gray-400`;
  }
}

/** Matches the Phase 1 / Phase 2 title row on markers so step circles align. */
function TitleSpacer() {
  return (
    <span className="mb-1 block h-4 w-full" aria-hidden>
      {"\u00a0"}
    </span>
  );
}

function Connector({ filled }: { filled: boolean }) {
  return (
    <div
      className="flex min-w-[0.5rem] flex-1 items-start pt-[calc(1rem+0.25rem+1rem)]"
      aria-hidden
    >
      {/* title (h-4 + mb-1) + half circle (h-8/2) ≈ align to circle center */}
      <div
        className={`h-0.5 w-full transition-colors duration-500 ${
          filled ? "bg-purple-600" : "bg-gray-200"
        }`}
      />
    </div>
  );
}

function StepNode({
  label,
  state,
  Icon,
  testId,
}: {
  label: string;
  state: VisualState;
  Icon: LucideIcon;
  testId?: string;
}) {
  return (
    <div
      className="flex w-16 flex-shrink-0 flex-col items-center"
      data-testid={testId}
      data-state={state}
      data-label={label}
    >
      <TitleSpacer />
      <div
        className={circleClass(state)}
        aria-current={
          state === "current" || state === "running" ? "step" : undefined
        }
      >
        {state === "done" ? (
          <Check className="h-4 w-4" strokeWidth={3} />
        ) : state === "skipped" ? (
          <Minus className="h-4 w-4" strokeWidth={3} />
        ) : (
          <Icon className="h-4 w-4" strokeWidth={2} />
        )}
      </div>
      <span className={labelClass(state)}>{label}</span>
    </div>
  );
}

function PhaseMarker({
  code,
  title,
  mode,
  filled,
  active,
  pulsing,
}: {
  code: "P1" | "P2";
  title: string;
  mode: string;
  filled: boolean;
  active: boolean;
  pulsing?: boolean;
}) {
  return (
    <div
      className="mx-0.5 flex w-16 flex-shrink-0 flex-col items-center"
      data-testid={`phase-marker-${code.toLowerCase()}`}
    >
      <span
        className={`mb-1 block h-4 text-center text-xs font-bold uppercase leading-none tracking-wide transition-colors duration-300 ${
          active || filled ? "text-purple-600" : "text-gray-500"
        }`}
      >
        {title}
      </span>
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[10px] font-bold transition-all duration-500 ${
          filled || active
            ? "border-purple-600 bg-white text-purple-600"
            : "border-dashed border-gray-300 bg-gray-50 text-gray-400"
        } ${pulsing ? "animate-pulse ring-2 ring-purple-200" : ""}`}
        title={
          code === "P1"
            ? "Phase 1 — manual confirmation steps"
            : "Phase 2 starts automatically after Promote"
        }
        aria-label={title}
      >
        {code}
      </div>
      <span
        className={`mt-1.5 text-[10px] font-medium ${
          active || filled ? "text-purple-600" : "text-gray-400"
        }`}
      >
        {mode}
      </span>
    </div>
  );
}

function phase2Visual(st: StepStatus | undefined): VisualState {
  if (st === "success") return "done";
  if (st === "running") return "running";
  if (st === "failed") return "failed";
  if (st === "skipped") return "skipped";
  return "todo";
}

function PhaseCard({
  children,
  emphasized,
  /** Relative width vs the other phase (by node count). */
  grow,
}: {
  children: ReactNode;
  emphasized?: boolean;
  grow: number;
}) {
  return (
    <div
      className={`min-w-0 rounded-lg border bg-white px-3 py-3 ${
        emphasized ? "border-purple-200 shadow-sm" : "border-gray-200"
      }`}
      style={{ flexGrow: grow, flexBasis: 0 }}
    >
      {children}
    </div>
  );
}

export function DataHubStepper({
  current,
  completed,
  failed,
  phase2StepStatus,
}: {
  current: StepId | "phase2" | "done";
  completed: Set<StepId>;
  failed?: Set<StepId>;
  /** When present, Phase 2 nodes render individually rather than as one chip. */
  phase2StepStatus?: Partial<Record<Phase2Step, StepStatus>>;
}) {
  const phase1State = (id: StepId): VisualState => {
    if (failed?.has(id)) return "failed";
    if (current === id) return "current";
    if (completed.has(id)) return "done";
    if ((current === "phase2" || current === "done") && completed.has(id)) {
      return "done";
    }
    return "todo";
  };

  const phase1DoneThrough = (index: number) => {
    const id = STEPS[index].id;
    const st = phase1State(id);
    return st === "done" || st === "failed";
  };

  const phase2Active =
    current === "phase2" ||
    current === "done" ||
    Object.values(phase2StepStatus ?? {}).some(
      s =>
        s === "running" || s === "success" || s === "failed" || s === "skipped"
    );

  const phase2Running = Object.values(phase2StepStatus ?? {}).some(
    s => s === "running"
  );

  const phase2FilledThrough = (index: number) => {
    const id = PHASE2_STEPS[index].id;
    const st = phase2Visual(phase2StepStatus?.[id]);
    return st === "done" || st === "skipped" || st === "failed";
  };

  const phase1Complete =
    current === "phase2" ||
    current === "done" ||
    (completed.has("promote") && current !== "promote");

  const phase1Active =
    current !== "phase2" && current !== "done" && !phase1Complete;

  // File is always done once the console opens (upload already happened).
  const p1MarkerFilled =
    completed.has("file") || phase1Active || phase1Complete;

  // Width share tracks node count (marker + steps) so gaps stay similar across
  // phases — Phase 1 is ~2/3–70% with the current step lists.
  const phase1Grow = STEPS.length + 1;
  const phase2Grow = PHASE2_STEPS.length + 1;

  return (
    <div
      className="flex w-full flex-col gap-3 xl:flex-row xl:items-stretch"
      data-testid="data-hub-stepper"
    >
      <PhaseCard emphasized={phase1Active} grow={phase1Grow}>
        <div className="flex w-full items-start overflow-x-auto pb-0.5">
          <PhaseMarker
            code="P1"
            title="Phase 1"
            mode="manual"
            filled={p1MarkerFilled}
            active={phase1Active}
          />
          <Connector filled={p1MarkerFilled} />
          {STEPS.map((s, i) => {
            const state = phase1State(s.id);
            return (
              <Fragment key={s.id}>
                <StepNode
                  label={s.label}
                  state={state}
                  Icon={s.icon}
                  testId={`step-${s.id}`}
                />
                {i < STEPS.length - 1 && (
                  <Connector filled={phase1DoneThrough(i)} />
                )}
              </Fragment>
            );
          })}
        </div>
      </PhaseCard>

      <PhaseCard emphasized={phase2Active && !phase1Active} grow={phase2Grow}>
        <div
          className={`flex w-full items-start overflow-x-auto pb-0.5 transition-opacity duration-500 ${
            phase2Active ? "opacity-100" : "opacity-55"
          }`}
        >
          <PhaseMarker
            code="P2"
            title="Phase 2"
            mode="auto"
            filled={phase1Complete}
            active={phase2Active}
            pulsing={phase2Running}
          />
          <Connector filled={phase1Complete} />
          {PHASE2_STEPS.map((s, i) => {
            const state = phase2Visual(phase2StepStatus?.[s.id]);
            return (
              <Fragment key={s.id}>
                <StepNode
                  label={s.label}
                  state={state}
                  Icon={s.icon}
                  testId={`step-${s.id}`}
                />
                {i < PHASE2_STEPS.length - 1 && (
                  <Connector filled={phase2FilledThrough(i)} />
                )}
              </Fragment>
            );
          })}
        </div>
      </PhaseCard>
    </div>
  );
}

export default DataHubStepper;
